<template>
  <button
      type="button"
      :id="id"
      class="tariff-lock-badge"
      :class="[`tariff-lock-badge--${badgeVariant}`, `tariff-lock-badge--${variant}`]"
      data-modal-autofocus-skip
      :aria-label="ariaLabel"
      :tabindex="keyboardFocusable ? null : -1"
      @click="handleClick"
  >
    <svg class="tariff-lock-badge__icon" width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
          class="tariff-lock-badge__icon-shackle"
          d="M4.5 7V5.25C4.5 3.45507 5.95507 2 7.75 2H8.25C10.0449 2 11.5 3.45507 11.5 5.25V7"
          stroke="currentColor"
          stroke-width="1.2"
          stroke-linecap="round"
          stroke-linejoin="round"
      />
      <path
          d="M3.8 7H12.2C12.6971 7 13 7.44772 13 8V13C13 13.5523 12.6971 14 12.2 14H3.8C3.30294 14 3 13.5523 3 13V8C3 7.44772 3.30294 7 3.8 7Z"
          stroke="currentColor"
          stroke-width="1.2"
          stroke-linecap="round"
          stroke-linejoin="round"
      />
    </svg>
    <span v-if="variant !== 'compact'" class="tariff-lock-badge__label">{{ label || defaultLabel }}</span>
  </button>
</template>

<script>
import { useTariffUpgrade } from "@/composables/useTariffUpgrade";
import { TARIFF_GATE_STATES } from "@/constants/tariffFeatureKeys";

export default {
  name: "TariffLockBadge",
  props: {
    gate: { type: Object, default: null },
    featureKey: { type: String, required: true },
    source: { type: String, default: null },
    quizId: { type: [String, Number], default: null },
    label: { type: String, default: "" },
    variant: {
      type: String,
      default: "default",
      validator: (value) => ["default", "compact"].includes(value),
    },
    id: { type: String, default: null },
    keyboardFocusable: { type: Boolean, default: true },
    forceLocked: { type: Boolean, default: false },
  },
  computed: {
    defaultLabel() {
      if (this.forceLocked) {
        return this.$t("tariffLockBadgeLabel");
      }
      if (this.gate?.state === TARIFF_GATE_STATES.GRANDFATHERED || this.gate?.state === TARIFF_GATE_STATES.DELETE_ONLY) {
        return this.$t("tariffLockBadgeReadOnlyLabel");
      }
      if (this.gate?.state === TARIFF_GATE_STATES.QUOTA_EXHAUSTED) {
        return this.$t("tariffLockBadgeQuotaLabel");
      }
      return this.$t("tariffLockBadgeLabel");
    },
    ariaLabel() {
      return this.$t("tariffLockBadgeAriaLabel", { label: this.defaultLabel });
    },
    badgeVariant() {
      if (this.forceLocked) {
        return "locked";
      }
      if (this.gate?.state === TARIFF_GATE_STATES.GRANDFATHERED || this.gate?.state === TARIFF_GATE_STATES.DELETE_ONLY || this.gate?.state === TARIFF_GATE_STATES.READ_ONLY) {
        return "info";
      }
      return "locked";
    },
  },
  methods: {
    handleClick() {
      const { openUpgrade } = useTariffUpgrade();
      openUpgrade({
        featureKey: this.featureKey,
        requiredValue: this.gate?.requiredValue || 1,
        source: this.source,
        quizId: this.quizId,
        action: this.gate?.action || null,
      });
    },
  },
};
</script>

<style scoped>
.tariff-lock-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: 1px solid transparent;
  border-radius: 12px;
  max-width: 160px;
  padding: 3px 8px;
  font-size: 11px;
  font-weight: 600;
  line-height: 14px;
  cursor: pointer;
  background: #FFE2A0;
  color: #A66A00;
  transition: background 0.16s ease, transform 0.16s ease;
}

.tariff-lock-badge:hover,
.tariff-lock-badge:focus-visible {
  background: #FFE2A0;
  outline: 1px solid gold;
}

.tariff-lock-badge:focus-visible {
  outline: 2px solid var(--color-main-blue, #3749c0);
  outline-offset: 2px;
}

.tariff-lock-badge--info {
  background: var(--color-main-selected-blue, #f3f4f9);
  color: var(--color-main-blue, #3749c0);
  max-width: 120px;
  flex-direction: row;
  justify-content: center;
  align-items: center;
}

.tariff-lock-badge--info:hover,
.tariff-lock-badge--info:focus-visible {
  background: var(--color-main-light-blue, #e6e8f1);
}

.tariff-lock-badge--compact {
  padding: 4px;
  gap: 0;
}

.tariff-lock-badge__icon {
  flex: 0 0 auto;
}

.tariff-lock-badge__icon-shackle {
  transform-box: fill-box;
  transform-origin: 100% 100%;
  transition: transform 0.18s ease;
}

.tariff-lock-badge:hover .tariff-lock-badge__icon-shackle,
.tariff-lock-badge:focus-visible .tariff-lock-badge__icon-shackle {
  transform: rotate(23deg) translateY(-0.5px);
}

.tariff-lock-badge__label {
  white-space: nowrap;
}
</style>
